/**
 * Mocking @theme/utilities for standalone functionality.
 * In a production environment, ensure these are imported from your actual utility paths.
 */
const mediaQueryLarge = window.matchMedia('(min-width: 990px)');
const isMobileBreakpoint = () => !mediaQueryLarge.matches;

/**
 * AccordionCustom
 * A custom web component that enhances the native <details> and <summary> elements
 * with responsive behavior, conditional disabling, and keyboard accessibility.
 * * Data Attributes:
 * - data-disable-on-mobile: "true" to prevent toggling on mobile view.
 * - data-disable-on-desktop: "true" to prevent toggling on desktop view.
 * - data-close-with-escape: "true" to allow closing the accordion with the Escape key.
 * - open-by-default-on-mobile: Attribute to set initial open state on mobile.
 * - open-by-default-on-desktop: Attribute to set initial open state on desktop.
 */
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('AccordionCustom: <details> element not found.');
    }
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) {
      throw new Error('AccordionCustom: <summary> element not found.');
    }
    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Initial state setup
    this.#setDefaultOpenState();

    // Event Listeners
    this.addEventListener('keydown', (e) => this.#handleKeyDown(e), { signal });
    this.summary.addEventListener('click', (e) => this.#handleClick(e), { signal });
    
    // Listen for window resize/media changes
    mediaQueryLarge.addEventListener('change', () => this.#handleMediaQueryChange(), { signal });
  }

  /**
   * Cleanup event listeners when the element is removed from the DOM.
   */
  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles click events on the summary.
   * Prevents toggling if the component is disabled for the current breakpoint.
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  }

  /**
   * Syncs the open state whenever the media query breakpoint is crossed.
   */
  #handleMediaQueryChange() {
    this.#setDefaultOpenState();
  }

  /**
   * Sets the default open state of the accordion based on the 
   * `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    const shouldBeOpen = (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
                         (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    this.details.open = shouldBeOpen;
  }

  /**
   * Handles keydown events for the accordion.
   * Closes the accordion and restores focus to the summary on 'Escape'.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape && this.details.open) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

// Register the custom element
if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}